import { useCallback, useEffect, useRef, useState } from "react";
import { IonSkeletonText } from "@ionic/react";
import { useQuery } from "@tanstack/react-query";
import { BoxShadows, Button } from "swing-components";

import { DocWorkflowAnswerType, GET, translate as t, type KeyWithEmptyFnParam } from "~utils";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Box: { ContentUploader: any };

type UploadAnswerProps = Extract<DocWorkflowAnswerType, { type: "UPLOAD" }> & {
  onChange?: (value?: string) => void;
  isReadOnly?: boolean;
};

export function UploadAnswer(props: UploadAnswerProps) {
  const { btn, onChange = () => {}, isReadOnly } = props;
  const buttonLabel = btn?.text ? t(btn.text as unknown as KeyWithEmptyFnParam) : t("LABEL_UPLOAD");

  // This API call upserts a folder into Box, and returns the folder
  // id and an auth token that can be used to upload files.
  const { data: boxValues } = useQuery({
    queryKey: ["getBoxValues"],
    queryFn: () => GET("/api/sub/upload-metadata"),
    select: (data) => data.data,
  });

  const onComplete = useCallback((fileId: string) => onChange(fileId), [onChange]);

  if (!boxValues) {
    return (
      <IonSkeletonText
        animated={true}
        css={{
          height: 36,
          width: "100%",
          borderRadius: 30,
        }}
      />
    );
  }

  return (
    <BoxUploader
      label={buttonLabel}
      folderId={boxValues.folderId}
      accessToken={boxValues.accessToken}
      onComplete={onComplete}
      isReadOnly={isReadOnly}
    />
  );
}

type BoxUploaderProps = {
  label: string;
  folderId: string;
  accessToken: string;
  onComplete: (fileId: string) => void;
  isReadOnly?: boolean;
};

/**
 * Displays the Box uploader UI which handles uploading files to Box.
 * When the upload is complete, the file ID is passed to the parent
 * component via `onComplete`.
 *
 * See https://developer.box.com/guides/embed/ui-elements/uploader
 */
function BoxUploader(props: BoxUploaderProps) {
  const { label, folderId, accessToken, onComplete, isReadOnly } = props;
  const [isOpen, setIsOpen] = useState(false);
  const contentUploaderRef = useRef<any>(null);

  function handleClick() {
    setIsOpen(true);
    contentUploaderRef.current.show(folderId, accessToken, {
      container: "#upload-modal",
      fileLimit: 1,
    });
  }

  useEffect(() => {
    const contentUploader = new Box.ContentUploader();
    contentUploaderRef.current = contentUploader;

    // The `complete` event returns an array of file metadata
    // https://developer.box.com/guides/embed/ui-elements/uploader/#events
    contentUploader.addListener("complete", (data: { id: string }[]) => {
      setIsOpen(false);
      onComplete(data[0].id);
    });

    contentUploader.addListener("close", () => {
      setIsOpen(false);
    });

    return () => {
      contentUploader.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Button
        type="primary"
        status={isReadOnly ? "disabled" : "enabled"}
        onClick={handleClick}
        isFluid
      >
        {label}
      </Button>

      <div
        id="upload-modal"
        css={{
          display: isOpen === true ? "block" : "none",
          position: "fixed",
          zIndex: 1,
          height: "80%",
          width: "80%",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: 8,
          boxShadow: BoxShadows.hover,
          "& > div": {
            borderRadius: 8,
          },
        }}
      />
    </div>
  );
}
